/**
 * useResolvedPageContext.ts
 *
 * Decides what the assistant thinks the user is looking at.
 *
 * Pages that call usePageContext() publish rich context (filters, data
 * summaries) and are always preferred. Pages that never did leave a *stale*
 * entry in the store — the previous page's route, name, filters and, worse,
 * its entity focus. That's how the sidebar ended up describing the last
 * patient you opened while you were sitting on an admin settings screen.
 *
 * The rule: if the stored context isn't for the route we're on, ignore it
 * entirely and derive a name from the route instead. Entity context is
 * dropped along with it, because only pages that register page context ever
 * set an entity.
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAISidebarStore, type PageContext } from '@/store/aiSidebarStore';
import type { ContextType } from '@/services/ai-context.service';
import { resolvePageName } from './pageNames';

export interface ResolvedPageContext {
  pageContext: PageContext;
  contextType: ContextType | null;
  contextId: number | null;
}

export function useResolvedPageContext(): ResolvedPageContext {
  const location = useLocation();
  const stored = useAISidebarStore((s) => s.pageContext);
  const contextType = useAISidebarStore((s) => s.contextType);
  const contextId = useAISidebarStore((s) => s.contextId);

  const isCurrent = stored?.route === location.pathname;

  return useMemo(
    () => ({
      pageContext: isCurrent
        ? (stored as PageContext)
        : { route: location.pathname, pageName: resolvePageName(location.pathname) },
      contextType: isCurrent ? contextType : null,
      contextId: isCurrent ? contextId : null,
    }),
    [isCurrent, stored, location.pathname, contextType, contextId],
  );
}
